/**
 * Database connection management.
 *
 * Handles Knex instance lifecycle: initialization, access, and cleanup.
 */

import knex, { type Knex } from 'knex'
import { getDatabaseConfig } from '@kernel/config/database.js'
import { setupQueryInterceptor } from './query-interceptor.js'

const globalForKnex = globalThis as unknown as { db: Knex | undefined }

/**
 * Initializes or reinitializes the DB connection
 */
export function initDb(): Knex {
  if (!globalForKnex.db) {
    const knexInstance = knex(getDatabaseConfig())
    globalForKnex.db = setupQueryInterceptor(knexInstance)
  }
  return globalForKnex.db
}

/**
 * Gets the current DB instance (lazy init).
 * Use instead of `db` to support restarts.
 */
export function getDb(): Knex {
  return initDb()
}

/**
 * @deprecated Use getDb() to support restart.
 * Proxy provides lazy initialization to avoid circular imports at module load time.
 */
 
export const db: Knex = new Proxy({} as Knex, {
  get(_, prop) { return (getDb() as unknown as Record<string | symbol, unknown>)[prop] }
})

/**
 * Closes the DB connection (for stop/cleanup)
 */
export async function destroyDb(): Promise<void> {
  if (globalForKnex.db) {
    await globalForKnex.db.destroy()
    globalForKnex.db = undefined
  }
}
